import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowLeft } from "lucide-react";
import { Navigation, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuilderRequest() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    markets_counties: "",
    buy_box_notes: "",
    close_timeline: "",
    good_faith_agreed: false
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.good_faith_agreed) {
      toast.error("Please agree to communicate in good faith.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads/builder-request`, formData);
      setSubmitted(true);
      toast.success("Request submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="builder-request-success">
        <Navigation mode="builder" />
        <div className="pt-24 pb-16 px-6 min-h-screen flex items-center justify-center">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-[#0F0F10] rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4">
              Request Received
            </h1>
            <p className="text-[#57534E] text-lg mb-8">
              We'll respond with clarity and next steps. Thank you for your integrity.
            </p>
            <Link to="/builder">
              <Button variant="outline" className="font-medium">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Builder Mode
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="builder-request-page">
      <SEO 
        title="Request Information Packet"
        description="Request an information packet from Steward Lots. Get details on available land opportunities for builders and buyers."
        path="/builder/request"
      />
      <Navigation mode="builder" />

      <div className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <Link to="/builder" className="text-[#57534E] hover:text-[#1C1917] inline-flex items-center gap-2 mb-6">
              <ArrowLeft className="w-4 h-4" />
              Back to Builder Mode
            </Link>
            <h1 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4">
              Request a Packet
            </h1>
            <p className="text-[#57534E] text-lg">
              Tell us about your business and the markets you're interested in.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="builder-request-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="input-name"
                />
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Optional"
                  data-testid="input-company"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="input-phone"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="markets_counties">Markets / Counties of Interest *</Label>
              <Textarea
                id="markets_counties"
                name="markets_counties"
                value={formData.markets_counties}
                onChange={handleChange}
                placeholder="List the markets, counties, or regions you're interested in"
                rows={3}
                required
                data-testid="input-markets"
              />
            </div>

            <div>
              <Label htmlFor="buy_box_notes">Buy Box Notes</Label>
              <Textarea
                id="buy_box_notes"
                name="buy_box_notes"
                value={formData.buy_box_notes}
                onChange={handleChange}
                placeholder="Any specific criteria or preferences (size, price range, etc.)"
                rows={3}
                data-testid="input-notes"
              />
            </div>

            <div>
              <Label htmlFor="close_timeline">Typical Close Timeline</Label>
              <Input
                id="close_timeline"
                name="close_timeline"
                value={formData.close_timeline}
                onChange={handleChange}
                placeholder="e.g., 30-45 days"
                data-testid="input-timeline"
              />
            </div>

            {/* Confirmation */}
            <div className="bg-[#F5F5F4] p-6 border border-[#E7E5E4]">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="good_faith_agreed"
                  checked={formData.good_faith_agreed}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, good_faith_agreed: checked }))}
                  data-testid="checkbox-good-faith"
                />
                <Label htmlFor="good_faith_agreed" className="text-sm cursor-pointer">
                  I agree to communicate in good faith and with integrity. *
                </Label>
              </div>
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              size="lg" 
              className="w-full bg-[#0F0F10] hover:bg-[#1C1917]"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
